type Rgb = { r: number; g: number; b: number };
type ColorPalette = {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  950: string;
};

type PaletteOptions = {
  lightenMask: Rgb;
  lightenWeights: [number, number, number, number, number];
  darkenMask: Rgb;
  darkenWeights: [number, number, number, number, number];
};

const paletteDefaults: PaletteOptions = {
  lightenMask: { r: 255, g: 255, b: 255 },
  darkenMask: { r: 0, g: 0, b: 0 },
  lightenWeights: [0.95, 0.8, 0.6, 0.4, 0.2],
  darkenWeights: [0.1, 0.2, 0.3, 0.4, 0.5],
};

export const hexToRgb = (hex: string): Rgb | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

export const rgbToHex = (color: Rgb): string =>
  `#${Math.round(color.r).toString(16).padStart(2, '0')}${Math.round(color.g).toString(16).padStart(2, '0')}${Math.round(color.b).toString(16).padStart(2, '0')}`;

export const mixColors = (color1: Rgb, color2: Rgb, weight: number): Rgb => ({
  r: Math.round(color1.r * (1 - weight) + color2.r * weight),
  g: Math.round(color1.g * (1 - weight) + color2.g * weight),
  b: Math.round(color1.b * (1 - weight) + color2.b * weight),
});

export const generateColorPalette = (
  baseColorHex: string,
  options: Partial<PaletteOptions> = {},
): ColorPalette => {
  const baseRgb = hexToRgb(baseColorHex);
  if (!baseRgb) {
    throw new Error('Invalid base color format.');
  }
  const { lightenMask, lightenWeights, darkenMask, darkenWeights } = {
    ...paletteDefaults,
    ...options,
  };

  return {
    50: rgbToHex(mixColors(baseRgb, lightenMask, lightenWeights[0])),
    100: rgbToHex(mixColors(baseRgb, lightenMask, lightenWeights[1])),
    200: rgbToHex(mixColors(baseRgb, lightenMask, lightenWeights[2])),
    300: rgbToHex(mixColors(baseRgb, lightenMask, lightenWeights[3])),
    400: rgbToHex(mixColors(baseRgb, lightenMask, lightenWeights[4])),
    500: baseColorHex,
    600: rgbToHex(mixColors(baseRgb, darkenMask, darkenWeights[0])),
    700: rgbToHex(mixColors(baseRgb, darkenMask, darkenWeights[1])),
    800: rgbToHex(mixColors(baseRgb, darkenMask, darkenWeights[2])),
    900: rgbToHex(mixColors(baseRgb, darkenMask, darkenWeights[3])),
    950: rgbToHex(mixColors(baseRgb, darkenMask, darkenWeights[4])),
  };
};

export const generatePaletteTokens = (colorName: string): ColorPalette => ({
  50: `{${colorName}.50}`,
  100: `{${colorName}.100}`,
  200: `{${colorName}.200}`,
  300: `{${colorName}.300}`,
  400: `{${colorName}.400}`,
  500: `{${colorName}.500}`,
  600: `{${colorName}.600}`,
  700: `{${colorName}.700}`,
  800: `{${colorName}.800}`,
  900: `{${colorName}.900}`,
  950: `{${colorName}.950}`,
});
